import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
  getDocsFromServer,
  serverTimestamp,
  type Timestamp,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { nanoid } from "nanoid";
import type { QRCodeData, ScanLocation } from "../types/qrcode";
import type { VCardCategory } from "../types/vcard";

interface FirestoreQRCodeDoc {
  userId: string;
  vcardId: string;
  category: VCardCategory;
  subCategory?: string;
  url: string;
  imageUrl?: string;
  version: number;
  createdAt: Timestamp;
  metadata?: {
    printHistory?: { date: Timestamp; format: string; quantity: number }[];
    lastScanned?: {
      latitude: number;
      longitude: number;
      timestamp: Timestamp;
      city?: string;
      country?: string;
      ip?: string;
    } | null;
    scanHistory?: {
      latitude: number;
      longitude: number;
      timestamp: Timestamp;
      city?: string;
      country?: string;
      ip?: string;
    }[];
  };
}

export class QRCodeService {
  private static readonly APP_ORIGIN = "https://tagify-it.com";

  // Deduplicates concurrent createQRCode calls for the same userId+vcardId
  // within the same browser session (prevents duplicate tokens on onboarding)
  private static readonly pendingCreates = new Map<
    string,
    Promise<QRCodeData>
  >();

  // Module-level token URL cache — survives component remounts so repeated
  // tab switches are instant without re-querying Firestore.
  // Key: "vcardId|userId" (userId may be empty string for anonymous callers).
  private static readonly tokenUrlCache = new Map<string, string | null>();

  private static generatePermanentUrl(qrId: string): string {
    return `${this.APP_ORIGIN}/qr/${qrId}`;
  }

  private static toDate(value: unknown): Date | null {
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }

    if (
      value &&
      typeof value === "object" &&
      "toDate" in value &&
      typeof value.toDate === "function"
    ) {
      try {
        const parsed: unknown = value.toDate();
        return parsed instanceof Date && !Number.isNaN(parsed.getTime())
          ? parsed
          : null;
      } catch {
        return null;
      }
    }

    return null;
  }

  private static async checkExistingQRCode(
    vcardId: string,
    userId?: string,
    forceServer = false,
  ): Promise<QRCodeData | null> {
    try {
      if (!userId) {
        console.warn("Skipping QR token lookup without userId");
        return null;
      }

      const q = query(collection(db, "qrcodes"), where("userId", "==", userId));
      // forceServer=true bypasses the local Firestore cache — used only during
      // token creation to prevent duplicates from concurrent writes.
      // Read-only callers (e.g. QRCodePreview via getTokenUrl) use the cache
      // so repeated tab switches are instant after the first load.
      const querySnapshot = forceServer
        ? await getDocsFromServer(q)
        : await getDocs(q);

      if (!querySnapshot.empty) {
        const docSnap = querySnapshot.docs.find((candidate) => {
          const data = candidate.data() as Partial<FirestoreQRCodeDoc>;
          return data.vcardId === vcardId;
        });

        if (!docSnap) {
          return null;
        }

        const data = docSnap.data() as FirestoreQRCodeDoc;

        const lastScannedTimestamp = this.toDate(
          data.metadata?.lastScanned?.timestamp,
        );
        const lastScanned =
          data.metadata?.lastScanned && lastScannedTimestamp
            ? ({
                ...data.metadata.lastScanned,
                timestamp: lastScannedTimestamp,
              } satisfies ScanLocation)
            : null;
        const scanHistory: ScanLocation[] = (
          data.metadata?.scanHistory ?? []
        ).flatMap((scan) => {
          const timestamp = this.toDate(scan.timestamp);
          return timestamp ? [{ ...scan, timestamp }] : [];
        });
        const printHistory =
          data.metadata?.printHistory?.flatMap((record) => {
            const date = this.toDate(record.date);
            return date ? [{ ...record, date }] : [];
          }) ?? [];
        return {
          ...data,
          id: docSnap.id,
          createdAt: this.toDate(data.createdAt) ?? data.createdAt,
          metadata: {
            printHistory,
            lastScanned,
            scanHistory,
          },
        } as QRCodeData;
      }

      return null;
    } catch (error) {
      console.error("Error checking existing QR code:", error);
      return null;
    }
  }

  static async createQRCode(params: {
    userId: string;
    vcardId: string;
    category: VCardCategory;
  }): Promise<QRCodeData> {
    // If a create is already in-flight for this user+vCard, share the promise
    const dedupKey = `${params.userId}|${params.vcardId}`;
    const pending = this.pendingCreates.get(dedupKey);
    if (pending) return pending;

    const operation = this._doCreateQRCode(params).finally(() => {
      this.pendingCreates.delete(dedupKey);
    });
    this.pendingCreates.set(dedupKey, operation);
    return operation;
  }

  private static async _doCreateQRCode(params: {
    userId: string;
    vcardId: string;
    category: VCardCategory;
  }): Promise<QRCodeData> {
    try {
      // Check for existing QR code — force server read to prevent duplicates
      // from concurrent writes during onboarding.
      const existingQRCode = await this.checkExistingQRCode(
        params.vcardId,
        params.userId,
        true,
      );
      if (existingQRCode) {
        // Repair any token that was created with a localhost URL
        if (existingQRCode.url.includes("localhost")) {
          const fixedUrl = this.generatePermanentUrl(existingQRCode.id);
          const docRef = doc(db, "qrcodes", existingQRCode.id);
          await setDoc(docRef, { url: fixedUrl }, { merge: true });
          console.log("Repaired localhost URL for QR code:", existingQRCode.id);
          const repaired = { ...existingQRCode, url: fixedUrl };
          this.tokenUrlCache.set(
            `${params.vcardId}|${params.userId}`,
            fixedUrl,
          );
          return repaired;
        }
        console.log("Found existing QR code for vCard:", params.vcardId);
        this.tokenUrlCache.set(
          `${params.vcardId}|${params.userId}`,
          existingQRCode.url,
        );
        return existingQRCode;
      }

      console.log("Creating new QR code for vCard:", params.vcardId);
      const qrId = nanoid(10);

      const qrData: QRCodeData = {
        id: qrId,
        userId: params.userId,
        vcardId: params.vcardId,
        category: params.category,
        url: this.generatePermanentUrl(qrId),
        version: 1,
        createdAt: serverTimestamp(),
        metadata: {
          printHistory: [],
          lastScanned: null,
          scanHistory: [],
        },
      };

      const docRef = doc(db, "qrcodes", qrId);
      await setDoc(docRef, {
        ...qrData,
        metadata: {
          ...qrData.metadata,
          scanHistory: [],
        },
      });

      this.tokenUrlCache.set(`${params.vcardId}|${params.userId}`, qrData.url);
      return qrData;
    } catch (error) {
      console.error("Error creating QR code:", error);
      throw new Error("Failed to create QR code");
    }
  }

  static async getTokenUrl(
    vcardId: string,
    userId?: string,
  ): Promise<string | null> {
    const cacheKey = `${vcardId}|${userId ?? ""}`;
    if (this.tokenUrlCache.has(cacheKey)) {
      return this.tokenUrlCache.get(cacheKey) ?? null;
    }
    const qrData = await this.checkExistingQRCode(vcardId, userId);
    const url = qrData?.url ?? null;
    this.tokenUrlCache.set(cacheKey, url);
    return url;
  }

  static async getQRCode(qrId: string): Promise<QRCodeData | null> {
    try {
      const docRef = doc(db, "qrcodes", qrId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) return null;

      const data = docSnap.data() as FirestoreQRCodeDoc;
      const lastScannedTimestamp = this.toDate(
        data.metadata?.lastScanned?.timestamp,
      );
      const lastScanned =
        data.metadata?.lastScanned && lastScannedTimestamp
          ? ({
              ...data.metadata.lastScanned,
              timestamp: lastScannedTimestamp,
            } satisfies ScanLocation)
          : null;
      const scanHistory: ScanLocation[] = (
        data.metadata?.scanHistory ?? []
      ).flatMap((scan) => {
        const timestamp = this.toDate(scan.timestamp);
        return timestamp ? [{ ...scan, timestamp }] : [];
      });
      const printHistory =
        data.metadata?.printHistory?.flatMap((record) => {
          const date = this.toDate(record.date);
          return date ? [{ ...record, date }] : [];
        }) ?? [];
      return {
        ...data,
        id: docSnap.id,
        createdAt: this.toDate(data.createdAt) ?? data.createdAt,
        metadata: {
          printHistory,
          lastScanned,
          scanHistory,
        },
      } as QRCodeData;
    } catch (error) {
      console.error("Error fetching QR code:", error);
      throw new Error("Failed to fetch QR code");
    }
  }
}
